import { useCallback, useEffect, useMemo, useState } from 'react';

export type DemoDockUser = {
    id?: string;
    name: string;
    provider?: string;
};

export type DemoDockAuthState = {
    status: 'loading' | 'authenticated' | 'unauthenticated' | 'error';
    user: DemoDockUser | null;
    error: string | null;
    loginUrl: string;
    loginScreenUrl: string;
    logoutUrl: string;
    refresh: () => void;
};

type AuthMeResponse = {
    authenticated: boolean;
    user?: DemoDockUser;
    clientPrincipal?: {
        userId?: string;
        userDetails?: string;
        identityProvider?: string;
        claims?: Array<{ typ?: string; val?: string }>;
    } | null;
};

const authCheckTimeoutMs = 10000;

export function getDemoDockProxyBaseUrl() {
    return process.env.REACT_APP_DEMO_DOCK_PROXY_URL?.replace(/\/$/, '') ?? '';
}

export function getDemoDockAuthBaseUrl() {
    const configuredBaseUrl = process.env.REACT_APP_DEMO_DOCK_AUTH_URL?.replace(/\/$/, '');
    if (configuredBaseUrl) {
        return configuredBaseUrl;
    }

    if (typeof window === 'undefined') {
        return '';
    }

    return getDemoDockProxyBaseUrl() ? window.location.origin : '';
}

export function getDemoDockProxyUrl(path: string) {
    const baseUrl = getDemoDockProxyBaseUrl();
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${normalizedPath}`;
}

function getDemoDockAuthUrl(path: string) {
    const baseUrl = getDemoDockAuthBaseUrl();
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${normalizedPath}`;
}

function getEmailClaim(claims: AuthMeResponse['clientPrincipal'] extends infer Principal
    ? Principal extends { claims?: infer Claims }
        ? Claims
        : never
    : never) {
    return claims?.find((claim) =>
        ['emails', 'preferred_username', 'email'].includes(claim.typ ?? '')
    )?.val;
}

function getUserFromAuthPayload(payload: AuthMeResponse): DemoDockUser | null {
    if (payload.authenticated) {
        return payload.user ?? null;
    }

    if (!payload.clientPrincipal) {
        return null;
    }

    const email = getEmailClaim(payload.clientPrincipal.claims);
    return {
        id: payload.clientPrincipal.userId,
        name: payload.clientPrincipal.userDetails || email || 'Authenticated user',
        provider: payload.clientPrincipal.identityProvider,
    };
}

function getReturnUrl() {
    if (typeof window === 'undefined') {
        return '/';
    }

    return window.location.href;
}

function getCurrentPath() {
    if (typeof window === 'undefined') {
        return '/projects/demodock';
    }

    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function getDemoDockLoginScreenUrl(returnTo = getCurrentPath()) {
    return `/projects/demodock/login-signup?returnTo=${encodeURIComponent(returnTo)}`;
}

export function getDemoDockLoginUrl(returnUrl = getReturnUrl()) {
    const baseUrl = getDemoDockAuthBaseUrl();
    if (!baseUrl) {
        return '';
    }

    return `${baseUrl}/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnUrl)}`;
}

export function getDemoDockLogoutUrl(returnUrl = getReturnUrl()) {
    const baseUrl = getDemoDockAuthBaseUrl();
    if (!baseUrl) {
        return '';
    }

    return `${baseUrl}/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(returnUrl)}`;
}

export function useDemoDockAuth(): DemoDockAuthState {
    const proxyBaseUrl = useMemo(getDemoDockProxyBaseUrl, []);
    const [reloadKey, setReloadKey] = useState(0);
    const [status, setStatus] = useState<DemoDockAuthState['status']>(
        proxyBaseUrl ? 'loading' : 'authenticated'
    );
    const [user, setUser] = useState<DemoDockUser | null>(
        proxyBaseUrl ? null : { name: 'Local development user', provider: 'local' }
    );
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(() => {
        setReloadKey((prev) => prev + 1);
    }, []);

    useEffect(() => {
        if (!proxyBaseUrl) {
            setStatus('authenticated');
            setUser({ name: 'Local development user', provider: 'local' });
            setError(null);
            return;
        }

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), authCheckTimeoutMs);
        setStatus('loading');
        setError(null);

        fetch(getDemoDockAuthUrl('/.auth/me'), {
            credentials: 'include',
            signal: controller.signal,
        })
            .then(async (response) => {
                if (response.status === 401) {
                    setStatus('unauthenticated');
                    setUser(null);
                    return null;
                }

                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || `Auth check failed with ${response.status}`);
                }

                return response.json() as Promise<AuthMeResponse>;
            })
            .then((payload) => {
                if (!payload) return;

                const nextUser = getUserFromAuthPayload(payload);
                setStatus(nextUser ? 'authenticated' : 'unauthenticated');
                setUser(nextUser);
            })
            .catch((err) => {
                if (controller.signal.aborted) return;
                setStatus('error');
                setUser(null);
                setError(err instanceof Error ? err.message : 'Unable to check Demo Dock sign-in');
            })
            .finally(() => {
                window.clearTimeout(timeout);
            });

        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [proxyBaseUrl, reloadKey]);

    return {
        status,
        user,
        error,
        loginUrl: getDemoDockLoginUrl(),
        loginScreenUrl: getDemoDockLoginScreenUrl(),
        logoutUrl: getDemoDockLogoutUrl(),
        refresh,
    };
}
